import React, { useState, useEffect } from "react";

import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { Nullable } from "primereact/ts-helpers";
import { Dropdown, DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { InputNumberChangeEvent } from 'primereact/inputnumber'
import { DataTable } from 'primereact/datatable';
import { confirmDialog } from 'primereact/confirmdialog';
import { Column } from 'primereact/column';

import NumberInput from "../../../Components/NumberInput";
import Textarea from "../../../Components/TextArea";
import DatePicker from "../../../Components/DatePicker";
import { checkNumber, checkText } from "../../../Utils/Validator";
import { formatDate, parseStringToDate } from "../../../Utils/getDate";
import { processCreateAPI, processDeleteAPI, processGetDataAPI, processUpdateAPI } from "../../../Api/Project/Progress";
import { Progress, Project } from "../../../Controllers/Types/Project/ProjectInterface";
import { useSelector } from "react-redux";
import { RootState } from "../../../Controllers/Store";
import { hasPermission } from "../../../Controllers/Selectors/permissionsSelector";
import { Slider, SliderChangeEvent } from "primereact/slider";

interface ProgressModalProps {
    isOpen: boolean;
    onClose: () => void;
    toast: React.RefObject<Toast>;
    selectedServiceName?: string;
    selectedServiceId?: number;
    projectDetails?: Project;
}

interface Status {
    name: string;
}

const ProgressModal: React.FC<ProgressModalProps> = ({ isOpen, onClose, toast, selectedServiceName, selectedServiceId, projectDetails }) => {
    const today = new Date();
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);
    const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
    const [selectedProgressId, setSelectedProgressId] = useState<number>();
    const statuses: Status[] = [
        {
            name: "Ongoing"
        },
        {
            name: "Completed"
        },
        {
            name: "Upcoming"
        }
    ];

    const [progressDataFullList, setProgressDataFullList] = useState<Progress[]>([]);

    /****************************** Progress form variables **********************************/
    const [stageNumber, setStageNumber] = useState<number | null>(null);
    const [description, setDescription] = useState<string>('');
    const [date, setDate] = useState<Nullable<Date>>(null);
    const [status, setStatus] = useState<Status | null>(null);
    const [percentage, setPercentage] = useState<number>(0);

    /* error massage */
    const [errorStageNumber, setErrorStageNumber] = useState<string>('');
    const [errorDescription, setErrorDescription] = useState<string>('');
    const [errorDate, setErrorDate] = useState<string>('');
    const [errorStatus, setErrorStatus] = useState<string>('');
    const [errorPercentage, setErrorPercentage] = useState<string>('');
    /* ************************************************************************************* */

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermissionadd_progress = useCanEdit('Progress', 'add_progress');
    const isPermissionedit_progress = useCanEdit('Progress', 'edit_progress');
    const isPermissiondelete_progress = useCanEdit('Progress', 'delete_progress');
    const isPermissionview_progress = useCanEdit('Progress', 'view_progress');

    useEffect(() => {
        progressDataGet();
    }, [selectedServiceId]);


    /* *********************************************** get progress data list *********************************************************************** */
    const progressDataGet = async () => {
        if (selectedServiceId) {
            const data = await processGetDataAPI(selectedServiceId || 0);
            if (data.status) {
                setProgressDataFullList(data.data || []);
            }
        }
    }
    /* *********************************************** get progress data list *********************************************************************** */


    /* *********************************************************************************************************************************************** */
    const handleCloseProgressModal = () => {
        setShowProgressModal(false);
        clearAllErrorMassage();
        clearAllInputValue();
    }


    /* **************************** clear All error massage *************************** */
    const clearAllErrorMassage = () => {
        setErrorStageNumber('');
        setErrorDescription('');
        setErrorDate('');
        setErrorStatus('');
        setErrorPercentage('');
    };
    /* **************************** clear All error massage *************************** */

    /* **************************** clear All input value *************************** */
    const clearAllInputValue = () => {
        setStageNumber(null);
        setDescription('');
        setDate(null);
        setStatus(null);
        setPercentage(0);
    };
    /* **************************** clear All input value *************************** */


    useEffect(() => {
        checkNumber(stageNumber!, false, setErrorStageNumber);
    }, [stageNumber]);

    useEffect(() => {
        checkText(description, false, setErrorDescription);
    }, [description]);

    useEffect(() => {
        checkText(formatDate(date!), false, setErrorDate);
    }, [date]);

    useEffect(() => {
        checkText(String(status?.name), false, setErrorStatus);
    }, [status]);

    useEffect(() => {
        checkNumber(percentage!, false, setErrorPercentage);
    }, [percentage]);

    function checkCredentials(dateValueOnly: string): boolean {
        const isValidStageNumber = checkNumber(stageNumber || 0, true, setErrorStageNumber);
        const isValidDescription = checkText(description, true, setErrorDescription);
        const isValidDate = checkText(dateValueOnly, true, setErrorDate);
        const isValidStatus = checkText(String(status?.name || ''), true, setErrorStatus);
        const isValidPercentage = checkNumber(percentage || 0, true, setErrorPercentage);

        return isValidStageNumber && isValidPercentage && isValidDescription && isValidDate && isValidStatus;
    }
    /* ****************************** Progress create part - start ********************************************** */

    const handleCreateProgress = async (e: React.FormEvent) => {
        e.preventDefault();

        clearAllErrorMassage();

        const dateValueOnly = formatDate(date || null);
        if (checkCredentials(dateValueOnly)) {
            setLoading(true);
            const data = await processCreateAPI(selectedServiceId || 0, stageNumber, description, status?.name || '', dateValueOnly, percentage);
            setLoading(false);

            if (data.statusCode === 200) {
                progressDataGet();
                handleCloseProgressModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Progress added.",
                        detail: "Progress added successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding the progress.",
                        life: 3000,
                    });
                }
            }
        }
    }
    /* ****************************** Progress create part - end ********************************************** */

    /* ****************************** Progress update part - start ********************************************** */
    function editProgressDataSet(progressData: Progress) {
        setStageNumber(progressData.stage_no);
        setDescription(progressData.description);
        setDate(parseStringToDate(progressData.date));
        setStatus(statuses.filter(status => status.name === progressData?.status)[0]);
        setPercentage(progressData.percentage);
    }

    const handleUpdateProgress = async (e: React.FormEvent) => {
        e.preventDefault();

        const dateValueOnly = formatDate(date || null);
        if (checkCredentials(dateValueOnly)) {
            setLoading(true);
            const data = await processUpdateAPI(selectedProgressId!, stageNumber, description, status?.name || '', dateValueOnly, percentage);
            setLoading(false);

            if (data.statusCode === 200) {
                progressDataGet();
                handleCloseProgressModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Progress updated.",
                        detail: "Progress updated successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error updating the progress.",
                        life: 3000,
                    });
                }
            }
        }
    }
    /* ****************************** Progress update part - end ********************************************** */


    /* ****************************** Progress delete part - start ********************************************** */
    const deleteProgressData = async (progressId: number) => {
        setLoading(true);
        const data = await processDeleteAPI(progressId);
        setLoading(false);

        if (data.statusCode === 200) {
            progressDataGet();

            setShowProgressModal(false);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Progress deleted.",
                    detail: "Progress deleted successfully.",
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the progress.",
                    life: 3000,
                });
            }
        }
    }
    /* ****************************** Progress delete part - end ********************************************** */
    /* *********************************************************************************************************************************************** */


    /************************************* For status ***************************************/
    /* For showing selected status */
    const selectedStatusTemplate = (option: Status, props: DropdownProps) => {
        if (option) {
            return (
                <div className="d-flex align-items-center">
                    <div>{option.name}</div>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    /* For showing statuses options in the dropdown list */
    const statusOptionTemplate = (option: Status) => {
        return (
            <div className="d-flex align-items-center">
                <div>{option.name}</div>
            </div>
        );
    };
    /**********************************************************************************************/

    /* Progress Modal header */
    const progressViewModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {selectedServiceName} Progress
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={onClose}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* For add progress */
    const handleAddProgress = () => {
        setDataState('Add');
        setShowProgressModal(true);
    }

    const handleStageNumberChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setStageNumber(e.value);
        }
    };

    const handleProgressChange = (e: SliderChangeEvent) => {
        const value = e.value;
        if (typeof value === 'number') {
            setPercentage(value);
        } else {
            setPercentage(0);
        }
    };

    const progressModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;Progress
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseProgressModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const progressModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseProgressModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateProgress : handleUpdateProgress}
                loading={loading}
                className="custom_btn primary"
            />
        </div>
    );
    /*  */

    /* For progress data */
    const handleDeleteProgressData = (progressId: number, stageNo: number): void => {
        confirmDialog({
            message: 'Are you sure you want to delete the stage ' + stageNo + ' details?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteProgressData(progressId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    /* Table body template */
    const stageNumberBody = (rowData: Progress) => {
        return (
            <span>Stage&nbsp;{rowData.stage_no}</span>
        )
    }

    const percentageBody = (rowData: Progress) => {
        return (
            <div className="percentage_display">
                <h6>{rowData.percentage}%</h6>
            </div>
        )
    }

    const editDataBody = (rowData: Progress) => {
        const progressDataId = rowData.progress_id;

        return (
            <button className="data_action_btn primary_table_btn p-ripple"
                disabled={!isPermissionedit_progress}
                onClick={() => {
                    setDataState('Edit');
                    setShowProgressModal(true);
                    setSelectedProgressId(progressDataId);
                    editProgressDataSet(rowData);
                }}>
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        )
    }

    const deleteDataBody = (rowData: Progress) => {
        const progressDataId = rowData.progress_id;
        const stageNo = rowData.stage_no;
        return (
            <button className="data_action_btn danger_table_btn p-ripple"
                disabled={!isPermissiondelete_progress}
                onClick={() => handleDeleteProgressData(progressDataId, stageNo)}>
                <i className="bi bi-trash3"></i>
                <Ripple />
            </button>
        )
    }

    const getRowClassName = (data: Progress, options: { [key: string]: any }) => {
        if (data.status === 'Ongoing') {
            return 'project_data_row ongoing';
        } else if (data.status === 'Completed') {
            return 'project_data_row completed';
        } else if (data.status === 'Upcoming') {
            return 'project_data_row upcoming';
        }
        return 'project_data_row';
    };
    /*  */
    /*  */

    return (
        <React.Fragment>
            {/* Show progress */}
            <Dialog
                visible={isOpen}
                header={progressViewModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_lg"
                onHide={onClose}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pt-sm-2 pb-sm-0 w-100">
                        <div className="row">
                            <div className="page_breadcrumb_area">
                                <ul className="page_breadcrumb">
                                    <li className="page_breadcrumb_item has_child">
                                        {projectDetails?.job_number} : {projectDetails?.address}
                                    </li>
                                    <li className="page_breadcrumb_item active">{selectedServiceName}&nbsp;Progress</li>
                                </ul>

                                {isPermissionadd_progress && <button className="add_data_link_btn p-ripple" onClick={handleAddProgress}>
                                    <i className="bi bi-plus-circle"></i>
                                    <span>Add progress</span>
                                    <Ripple />
                                </button>}
                            </div>

                            <div className="modal_body_content_area">
                                {progressDataFullList?.length > 0 ? (
                                    <DataTable
                                        value={progressDataFullList}
                                        paginator
                                        sortMode="single"
                                        size="small"
                                        rows={5}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        tableStyle={{ minWidth: '50rem' }}
                                        paginatorDropdownAppendTo={'self'}
                                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                        className="page_table p-0 p-sm-2"
                                        rowHover
                                        rowClassName={getRowClassName}
                                    >

                                        <Column header="Stage" body={stageNumberBody} field="job_number" style={{ width: '15%' }}></Column>

                                        <Column header="Description" field="description" style={{ width: '30%' }}></Column>

                                        <Column header="Date" field="date" style={{ width: '15%' }}></Column>

                                        <Column header="Percentage" alignHeader="center" body={percentageBody} className="percentage_display_area" style={{ width: '20%' }}></Column>

                                        <Column header="Edit" body={editDataBody} alignHeader="center" className="data_action_btn_area" style={{ width: '10%' }}></Column>

                                        <Column header="Delete" body={deleteDataBody} alignHeader="center" className="data_action_btn_area" style={{ width: '10%' }}></Column>

                                    </DataTable>

                                ) : (
                                    <div className="modal_no_content_area" style={{ minHeight: '60vh' }}>
                                        <img src="/assets/images/no_data/no_data_img.png" alt="No Data Found!" />
                                        <h6>No progress has been added to Add the progress enter the data.</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog >
            {/*  */}

            {/* Add / Edit Progress */}
            <Dialog
                header={progressModalHeader}
                headerClassName="custom_modal_header"
                footer={progressModalFooter}
                visible={showProgressModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseProgressModal}
                dismissableMask
            >
                <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                    <div className="row">
                        {/* Stage number */}
                        <div className="col-12">
                            <NumberInput
                                inputId="stageNumber"
                                label="Stage number"
                                labelHtmlFor="stageNumber"
                                placeholder="Stage number"
                                required={true}
                                value={stageNumber}
                                onChange={handleStageNumberChange}
                                error={errorStageNumber}
                            />
                        </div>

                        {/* Description */}
                        <div className="col-12">
                            <Textarea
                                id="description"
                                label="Description"
                                labelHtmlFor="description"
                                required={true}
                                value={description}
                                placeholder="Description"
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                                error={errorDescription}
                            />
                        </div>

                        {/* Date */}
                        <div className="col-12">
                            <DatePicker
                                id="date"
                                label="Date"
                                labelHtmlFor="date"
                                required={true}
                                value={date}
                                minDate={today}
                                placeholder="dd/mm/yyyy"
                                onChange={setDate}
                                error={errorDate}
                                showClear
                                onClear={() => setDate(null)}
                            />
                        </div>

                        {/* Status */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                <Dropdown value={status} id="status" onChange={(e: DropdownChangeEvent) => setStatus(e.value)}
                                    options={statuses} optionLabel="name" valueTemplate={selectedStatusTemplate} itemTemplate={statusOptionTemplate}
                                    placeholder="Select status" className="form_dropdown w-100" showClear />

                                {errorStatus &&
                                    <small className="form_error_msg">{errorStatus}</small>
                                }
                            </div>
                        </div>

                        {/* Add percentage */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="percentage" className="custom_form_label is_required">Change the percentage</label>
                                <div className="progress_display">
                                    <h6>Progress:</h6>
                                    <span>{percentage}%</span>
                                </div>
                                <Slider
                                    value={percentage}
                                    min={0}
                                    max={100}
                                    onChange={handleProgressChange}
                                    step={1}
                                    className="w-14rem custom_progress_bar"
                                />

                                {errorPercentage &&
                                    <small className="form_error_msg mt-3">{errorPercentage}</small>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog >
            {/*  */}
        </React.Fragment>
    )
}

export default ProgressModal;